'use client'

import { useState, useRef, useTransition } from 'react'
import { Upload, X, ImageIcon } from 'lucide-react'

interface BannerUploadProps {
  currentBannerUrl: string | null
  companyId: string
  onUploaded: (url: string) => void
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export function BannerUpload({ currentBannerUrl, companyId, onUploaded }: BannerUploadProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [preview, setPreview] = useState<string | null>(currentBannerUrl)
  const [error, setError] = useState('')
  const [isPending, startTransition] = useTransition()
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFile = (file: File) => {
    setError('')

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Only JPG, PNG, and WebP images are supported.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be under 5MB.')
      return
    }

    // Preview
    const reader = new FileReader()
    reader.onload = (e) => setPreview(e.target?.result as string)
    reader.readAsDataURL(file)

    // Upload
    startTransition(async () => {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('companyId', companyId)

        const { uploadCompanyBanner } = await import('../_actions/banner-actions')
        const result = await uploadCompanyBanner(formData)
        if (result.success && result.data) {
          onUploaded(result.data.bannerUrl)
        } else if (!result.success) {
          setError(result.error)
          setPreview(currentBannerUrl)
        }
      } catch {
        setError('Upload failed. Please try again.')
        setPreview(currentBannerUrl)
      }
    })
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-[var(--color-text-primary)]" style={font}>
        Company Banner
      </label>
      <p className="text-xs text-[var(--color-text-muted)]" style={font}>
        Recommended: 1500&times;500px (3:1 ratio). Max 5MB. JPG, PNG, or WebP.
      </p>

      {preview ? (
        <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '3/1', maxHeight: '200px' }}>
          <img src={preview} alt="Banner preview" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={() => {
              setPreview(null)
              if (fileRef.current) fileRef.current.value = ''
            }}
            className="absolute right-2 top-2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-page)] py-8 transition-colors hover:border-[var(--color-brand-indigo)]"
        >
          <ImageIcon className="h-8 w-8 text-[var(--color-text-muted)]" />
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]" style={font}>
            Drag &amp; drop or click to upload
          </p>
          <div className="mt-2 flex items-center gap-1 text-xs text-[var(--color-brand-indigo)]" style={font}>
            <Upload className="h-3 w-3" />
            Browse files
          </div>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
        }}
      />

      {isPending && (
        <p className="text-xs text-[var(--color-text-muted)]" style={font}>Uploading...</p>
      )}
      {error && (
        <p className="text-xs text-[var(--color-error)]" style={font}>{error}</p>
      )}
    </div>
  )
}
